import { lazy, ComponentType, createElement } from "react";

/**
 * ============================================================================
 * ROUTE MAPPING & COMPONENT REGISTRY SYSTEM
 * ============================================================================
 *
 * This module provides dynamic routing for menu-driven navigation.
 * It automatically discovers components and maps API routes with intelligent matching.
 *
 * FEATURES:
 * - Automatic component discovery via Webpack require.context
 * - Flexible route matching (exact, case-insensitive, partial, fuzzy)
 * - PUBLIC_URL handling for all deployment environments
 * - Consistent kebab-case URL generation
 *
 * ROUTE TRANSFORMATION EXAMPLES:
 *   API Route                                  → Browser URL
 *   /components/oilProduction/main            → {PUBLIC_URL}/app/oil-production
 *   /components/DesiriBulls/main              → {PUBLIC_URL}/app/desiri-bulls
 *   components/CustomerReport/main            → {PUBLIC_URL}/app/customer-report
 *
 * NOTE: All dynamic routes are prefixed with '/app/' to avoid conflicts with
 * static resources in the public folder (e.g., banners.json, products.json)
 */

// ============================================================================
// CONSTANTS & TYPES
// ============================================================================

type ComponentRegistry = Record<string, ComponentType<any>>;

const PUBLIC_URL = (process.env.PUBLIC_URL || "").replace(/\/+$/g, "");

// Unique prefix for all dynamic routes to avoid conflicts with static resources
const DYNAMIC_ROUTE_PREFIX = "farm2me";

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Converts a string to kebab-case
 * Handles PascalCase, camelCase, and already hyphenated strings
 */
const toKebabCase = (str: string): string => {
  return str
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1-$2")
    .toLowerCase()
    .replace(/[_\s]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/**
 * Converts displayName to URL-safe path
 * Example: "Raw Material Purchase" -> "raw-material-purchase"
 */
export const displayNameToUrlPath = (displayName: string): string => {
  if (!displayName) return "";
  
  return displayName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Remove consecutive hyphens
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
};

/**
 * Normalizes and extracts component key from route path
 */
const extractComponentKey = (route: string): string => {
  if (!route) return "";

  return route
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .replace(/^components\//i, "");
};

/**
 * Creates error component for failed lazy loads
 */
const createErrorComponent = (componentKey: string): ComponentType<any> => {
  return () =>
    createElement(
      "div",
      { style: { padding: "2rem", textAlign: "center" } },
      createElement("h3", null, "Component Load Error"),
      createElement("p", null, `Failed to load: ${componentKey}`)
    );
};

// ============================================================================
// COMPONENT REGISTRY
// ============================================================================

/**
 * Creates component registry using Webpack's require.context
 */
const createComponentRegistry = (): ComponentRegistry => {
  const registry: ComponentRegistry = {};

  try {
    // @ts-ignore - Webpack specific API
    const requireComponent = require.context("../../", true, /^(?!.*\.(?:test|spec)\.tsx$).*\.tsx$/);

    requireComponent.keys().forEach((fileName: string) => {
      const componentKey = fileName.replace(/^\.\//, "").replace(/\.tsx$/, "");

      // Skip index, test, and spec files
      if (/\.(index|test|spec)/.test(componentKey)) {
        return;
      }

      registry[componentKey] = lazy(async () => {
        try {
          const module = await import(/* webpackExclude: /\.(test|spec)\.[jt]sx?$/ */ `../../${componentKey}`);

          // Prefer default export, otherwise fall back to the first exported member
          const fallback = Object.values(module)[0] as ComponentType<any> | undefined;
          const resolved = (module as { default?: ComponentType<any> }).default || fallback;

          if (typeof resolved === "function") {
            return { default: resolved };
          }

          console.error(
            `[RouteMapping] Module loaded without a component export: ${componentKey}`
          );
          return { default: createErrorComponent(componentKey) };
        } catch (error) {
          console.error(`[RouteMapping] Failed to load component: ${componentKey}`, error);
          return { default: createErrorComponent(componentKey) };
        }
      });
    });
  } catch (error) {
    console.error("[RouteMapping] Error creating component registry:", error);
  }

  return registry;
};

const componentRegistry = createComponentRegistry();
const componentCache = new Map<string, ComponentType<any>>();

// ============================================================================
// COMPONENT LOOKUP STRATEGIES
// ============================================================================

type MatchStrategy = (key: string, registryKeys: string[]) => string | undefined;

const matchStrategies: MatchStrategy[] = [
  // Strategy 1: Exact match
  (key, registryKeys) => registryKeys.find((k) => k === key),

  // Strategy 2: Case-insensitive match
  (key, registryKeys) => {
    const lowerKey = key.toLowerCase();
    return registryKeys.find((k) => k.toLowerCase() === lowerKey);
  },

  // Strategy 3: Filename match (last part of path)
  (key, registryKeys) => {
    const fileName = key.split("/").pop()?.toLowerCase();
    if (!fileName) return undefined;
    return registryKeys.find(
      (k) => k.split("/").pop()?.toLowerCase() === fileName
    );
  },

  // Strategy 4: Partial path match
  (key, registryKeys) => {
    const lowerKey = key.toLowerCase();
    return registryKeys.find(
      (k) =>
        k.toLowerCase().includes(lowerKey) ||
        lowerKey.includes(k.toLowerCase())
    );
  },

  // Strategy 5: Fuzzy match (ignore hyphens, underscores, slashes)
  (key, registryKeys) => {
    const normalizedKey = key.replace(/[-_/]/g, "").toLowerCase();
    return registryKeys.find(
      (k) => k.replace(/[-_/]/g, "").toLowerCase() === normalizedKey
    );
  },
];

/**
 * Finds component in registry using multiple matching strategies
 */
const findComponentInRegistry = (
  route: string
): ComponentType<any> | undefined => {
  const componentKey = extractComponentKey(route);
  if (!componentKey) return undefined;

  const registryKeys = Object.keys(componentRegistry);

  for (const strategy of matchStrategies) {
    const match = strategy(componentKey, registryKeys);
    if (match) {
      return componentRegistry[match];
    }
  }

  return undefined;
};

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Gets component for a given route from registry with caching
 */
export const getComponentForRoute = (
  route: string
): ComponentType<any> | undefined => {
  if (!route) return undefined;

  const key = route.trim();
  if (componentCache.has(key)) {
    return componentCache.get(key);
  }

  const component = findComponentInRegistry(route);
  if (component) {
    componentCache.set(key, component);
  }

  return component;
};

/**
 * Converts component path to URL-safe router path
 * Prefers displayName for URL generation when provided
 * Adds unique prefix to avoid conflicts with static resources
 *
 * Examples:
 *   displayName: "Raw Material Purchase" → {PUBLIC_URL}/app/raw-material-purchase
 *   displayName: "Customer Report" → {PUBLIC_URL}/app/customer-report
 *   componentPath: /components/oilProduction/main → {PUBLIC_URL}/app/oil-production
 */
export const getUrlPathFromComponentPath = (
  componentPath: string,
  displayName?: string
): string => {
  if (!componentPath) {
    return PUBLIC_URL || "/";
  }

  let path = "";

  // Prefer displayName for URL generation
  if (displayName && displayName.trim()) {
    path = displayNameToUrlPath(displayName);
  } else {
    // Fallback to component path-based URL
    path = componentPath
      .trim()
      .replace(/^\/+|\/+$/g, "")
      .replace(/^components\//i, "")
      .replace(/\/(main|index)$/i, "");

    if (!path) {
      return PUBLIC_URL || "/";
    }

    // Transform path to kebab-case URL
    if (path.includes("/")) {
      // Multi-level path: join all parts with hyphens
      path = path
        .split("/")
        .map((part) => toKebabCase(part))
        .join("-");
    } else {
      // Single-level path: just convert to kebab-case
      path = toKebabCase(path);
    }
  }

  // Clean up consecutive hyphens
  path = path.replace(/-+/g, "-").replace(/^-+|-+$/g, "");

  // Add dynamic route prefix to avoid conflicts with static resources
  const fullPath = `${DYNAMIC_ROUTE_PREFIX}/${path}`;

  return PUBLIC_URL ? `${PUBLIC_URL}/${fullPath}` : `/${fullPath}`;
};

/**
 * Clears the component cache - useful for hot reloading
 */
export const clearComponentCache = (): void => {
  componentCache.clear();
};
